// FreelancerProfileModal.jsx - Versão atualizada

import React, { useState, useEffect } from 'react';
import './FreelancerProfileModal.scss';
import AccountStatusSection from '../Accountstatussection/Accountstatussection';
import { uploadAvatar, uploadDocument } from '../../services/cloudinaryService';
import { logout } from '../../services/authService';
import { useNavigate, Link } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import Toast from "../Toast/Toast";
import { Lock } from "lucide-react"

// ─── Helpers de formatação ────────────────────────────────────────────────────
function formatarTelefone(value) {
  const n = value.replace(/\D/g, '').slice(0, 11);
  if (n.length <= 10) return n.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3').trim().replace(/-$/, '');
  return n.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3').trim().replace(/-$/, '');
}

function formatarCNPJ(value) {
  return value.replace(/\D/g, '').slice(0, 14)
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

function formatarCEP(value) {
  return value.replace(/\D/g, '').slice(0, 8).replace(/(\d{5})(\d{1,3})/, '$1-$2');
}
// ─── Ícones inline (SVG) ──────────────────────────────────────────────────────
const IconUpload = () => (
  <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <polyline points="16 16 12 12 8 16" /><line x1="12" y1="12" x2="12" y2="21" />
    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
  </svg>
);

const IconCamera = () => (
  <svg width="36" height="36" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

const IconPlus = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" viewBox="0 0 24 24">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const IconTrash = () => (
  <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" />
  </svg>
);

// ─── Componente principal ─────────────────────────────────────────────────────
const FreelancerProfileModal = ({
  open,
  onClose,
  user,
  atualizarPerfil,
  AddressAutocomplete,
  onAvatarUpdate
}) => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploadingDocFront, setUploadingDocFront] = useState(false);
  const [uploadingDocBack, setUploadingDocBack] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    phone: '',

    cep: '',
    logradouro: '',
    bairro: '',
    cidade: '',
    estado: '',
    numero: '',
    complemento: '',

    cpf: '',
    cnpj: '',
    avatarUrl: '',
    gender: null,
    nomeSocial: '',
    searchRadius: 50,
    bio: '',
    areas: [],
    workExperiences: [],

    documentType: 'rg',
    documentUrlFront: '',
    documentUrlBack: '',
    documentStatus: 'pending',
  });

  const [toast, setToast] = useState({ message: "", type: "success" });
  const [loadingCep, setLoadingCep] = useState(false);

  const buscarCEP = async (cep) => {
    const cepLimpo = cep.replace(/\D/g, "");

    if (cepLimpo.length !== 8) return;

    setLoadingCep(true);

    try {
      const response = await fetch(
        `https://brasilapi.com.br/api/cep/v2/${cepLimpo}`
      );

      if (!response.ok) {
        showToast("CEP não encontrado.", "error");
        return;
      }

      const data = await response.json();

      const lat = data.location?.coordinates?.latitude;
      const lng = data.location?.coordinates?.longitude;

      setFormData((prev) => ({
        ...prev,

        cep: data.cep || cepLimpo,
        logradouro: data.street || "",
        bairro: data.neighborhood || "",
        cidade: data.city || "",
        estado: data.state || "",

        lat: lat != null ? Number(lat) : prev.lat,
        lng: lng != null ? Number(lng) : prev.lng,
      }));

      // Se a BrasilAPI não retornar coordenadas,
      // deixa o backend resolver posteriormente.
      if (lat == null || lng == null) {
        // console.log("CEP encontrado, mas sem coordenadas.");
      }

    } catch (error) {
      console.error("Erro ao buscar CEP:", error);

      showToast(
        "Não foi possível buscar o CEP agora. Tente novamente.",
        "error"
      );
    } finally {
      setLoadingCep(false);
    }
  };

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  useEffect(() => {
    if (user && open) {
      //PEGA OS DADOS DE ENDEREÇO DA NOVA TABELA
      const address = user.address || user.addressData || {};

      setFormData({
        name: user.name || '',
        phone: user.phone || '',

        // ─── ENDEREÇO (AGORA VEM DA TABELA SEPARADA) ───
        cep: address.cep || user.cep || '',
        logradouro: address.logradouro || user.logradouro || '',
        numero: address.numero || user.numero || '',
        complemento: address.complemento || user.complemento || '',
        bairro: address.bairro || user.bairro || '',
        cidade: address.cidade || user.cidade || '',
        estado: address.estado || user.estado || '',
        lat: address.lat ?? address.latitude ?? user.lat ?? undefined,
        lng: address.lng ?? address.longitude ?? user.lng ?? undefined,

        // ─── DADOS PESSOAIS ───
        cpf: user.cpf || '',
        cnpj: user.cnpj || '',
        avatarUrl: user.avatarUrl || '',
        gender: user.gender || null,
        nomeSocial: user.nomeSocial || '',
        searchRadius: user.searchRadius || 50,
        bio: user.bio || '',

        areas: user.areas || user.skills?.map(s => s.name ?? s) || [],

        workExperiences: user.workExperiences || [],

        // ─── DOCUMENTOS ───
        documentType: user.documentType || 'rg',
        documentUrlFront: user.documentUrlFront || '',
        documentUrlBack: user.documentUrlBack || '',
        documentStatus: user.documentStatus || 'pending',
      });

      setPreviewUrl(user.avatarUrl || '');
    }
  }, [user, open]);

  if (!open || !user) return null;

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const isFreelancer = user.userType === "Freelancer";
  const isEmpresa = user.userType === "Company";
  const isAdmin = user.userType === "Admin";
  const showFreelancerFields = isFreelancer || isAdmin;
  const needsBack = formData.documentType !== 'cpf' && formData.documentType !== 'cnh_digital';

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.cep || formData.cep.replace(/\D/g, '').length !== 8) {
      showToast("Informe um CEP válido.", "error");
      return;
    }

    // if (!formData.logradouro) {
    //   showToast("Informe o endereço.", "error");
    //   return;
    // }

    // if (!formData.numero) {
    //   showToast("Informe o número do endereço.", "error");
    //   return;
    // }

    setLoading(true);

    const payload = {
      name: formData.name,
      phone: formData.phone,
      cnpj: formData.cnpj.replace(/\D/g, ''),
      avatarUrl: formData.avatarUrl,
      gender: formData.gender,
      nomeSocial: formData.nomeSocial,
      searchRadius: formData.searchRadius,
      bio: formData.bio,

      lat: formData.lat ?? null,
      lng: formData.lng ?? null,

      documentType: formData.documentType,
      documentUrlFront: formData.documentUrlFront,
      documentUrlBack: formData.documentUrlBack,
      documentStatus: formData.documentStatus,

      areas: formData.areas,

      workExperiences: formData.workExperiences,

      address: {
        cep: formData.cep.replace(/\D/g, ''),
        logradouro: formData.logradouro,
        numero: formData.numero,
        complemento: formData.complemento || null,
        bairro: formData.bairro,
        cidade: formData.cidade,
        estado: formData.estado,
      },
    };

    const result = await atualizarPerfil?.(payload);

    setLoading(false);

    if (result?.success) {
      showToast(result.message, "success");
      onClose();
    } else {
      showToast(
        result?.message ?? "Erro ao atualizar perfil.",
        "error"
      );
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showToast("Por favor, selecione uma imagem válida.", "error");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showToast("A imagem deve ter no máximo 5MB.", "error");
      return;
    }

    setUploading(true);

    const localPreview = URL.createObjectURL(file);
    setPreviewUrl(localPreview);

    try {
      const { url } = await uploadAvatar(user.id, file);

      setPreviewUrl(url);
      setFormData(prev => ({ ...prev, avatarUrl: url }));

      if (onAvatarUpdate) {
        onAvatarUpdate(url);
      }

      showToast("Foto de perfil atualizada com sucesso", "success");
    } catch (err) {
      console.error('Erro ao enviar avatar:', err);
      setPreviewUrl(formData.avatarUrl || '');
      showToast(
        err.response?.data?.message || 'Não foi possível enviar a foto. Tente novamente.',
        "error"
      );
    } finally {
      setUploading(false);
      if (localPreview) {
        URL.revokeObjectURL(localPreview);
      }
    }
  };

  const uploadDoc = async (file, side) => {
    const setUpl = side === 'front' ? setUploadingDocFront : setUploadingDocBack;
    const field = side === 'front' ? 'documentUrlFront' : 'documentUrlBack';

    if (file.size > 5 * 1024 * 1024) {
      showToast("O arquivo deve ter no máximo 5MB", "error");
      return;
    }

    setUpl(true);

    try {
      const { url } = await uploadDocument(user.id, file, formData.documentType, side);
      setFormData(prev => ({ ...prev, [field]: url, documentStatus: 'pending' }));
      showToast(`Foto ${side === 'front' ? 'da Frente' : 'do Verso'} carregada e enviada para análise.`, "success");
    } catch (err) {
      console.error('Erro ao enviar documento:', err);
      showToast(
        err.response?.data?.message || 'Não foi possível enviar o documento.',
        "error"
      );
    } finally {
      setUpl(false);
    }
  };

  const handleAddExperience = () => {
    set('workExperiences', [
      ...formData.workExperiences,
      { id: Math.random().toString(36).substr(2, 9), companyName: '', position: '', workMonth: '', workYear: '' },
    ]);
  };

  const handleUpdateExperience = (id, field, value) => {
    set('workExperiences', formData.workExperiences.map(exp => exp.id === id ? { ...exp, [field]: value } : exp));
  };

  const handleRemoveExperience = (id) => {
    set('workExperiences', formData.workExperiences.filter(exp => exp.id !== id));
  };

  const docStatusClass = {
    approved: 'pem-badge--approved',
    rejected: 'pem-badge--rejected',
    pending: 'pem-badge--pending',
  }[formData.documentStatus] || 'pem-badge--pending';

  const docStatusLabel = {
    approved: 'Aprovado',
    rejected: 'Rejeitado ❌',
    pending: 'Em Análise ⏳',
  }[formData.documentStatus] || 'Em Análise ⏳';

  // ─── FUNÇÃO DE LOGOUT ATUALIZADA PARA ADMIN ──────────────────────────────
  const handleLogout = async () => {
    // Confirmação antes de sair
    const confirmLogout = window.confirm(
      isAdmin
        ? 'Tem certeza que deseja sair da conta de Administrador?'
        : 'Tem certeza que deseja sair da sua conta?'
    );

    if (!confirmLogout) return;

    try {
      // Chama o logout do servidor para invalidar o refreshToken
      await logout();
    } catch (error) {
      console.error('Erro ao encerrar sessão no servidor:', error);
    } finally {
      // Limpa TODOS os dados do localStorage
      localStorage.clear();

      // Limpa cookies (incluindo refreshToken)
      document.cookie.split(";").forEach(c => {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });

      // Fecha o modal se estiver aberto
      if (onClose) onClose();

      // Redireciona para a página inicial
      navigate('/', { replace: true });
    }
  };

  return (
    <div className="pem-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="pem-modal">

        {/* Header */}
        <div className="pem-header">
          <h2 className="pem-title">
            {isAdmin ? 'Painel do Administrador' : 'Editar Perfil'}
          </h2>
          <button className="pem-close" onClick={onClose} aria-label="Fechar">×</button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="pem-body">

          {/* ── Avatar ── */}
          <div className="pem-avatar-section">
            <div className="pem-avatar-wrap">
              <div className="pem-avatar">
                {previewUrl
                  ? <img src={previewUrl} alt="Preview" className="pem-avatar-img" />
                  : <span className="pem-avatar-icon"><IconCamera /></span>
                }
                {uploading && <div className="pem-avatar-loading"><div className="pem-spinner" /></div>}
              </div>
              <label htmlFor="avatar-upload" className="pem-avatar-btn" title="Alterar foto">
                <IconUpload />
              </label>
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                className="pem-hidden-input"
                onChange={handleFileChange}
                disabled={uploading}
              />
            </div>
            <p className="pem-avatar-hint">Clique no ícone para alterar sua foto</p>
          </div>

          {showFreelancerFields && (
            <div className="pem-account-status">
              <AccountStatusSection
                freelancerId={user.id}
                accountCreatedAt={user.createdAt}
                buttonText="Status da conta"
                buttonClassName="pem-account-link"
              />
            </div>
          )}

          {/* ── Nome ── */}
          <div className="pem-field">
            <label className="pem-label">Nome Completo</label>
            <input
              className="pem-input"
              value={formData.name}
              required
              placeholder="Ex: João Silva ou Empresa LTDA"
              disabled
            />
            <p className="pem-hint">Para alterar seu nome, entre em contato com o suporte.</p>
          </div>

          {showFreelancerFields && (
            <div className="pem-field">
              <label className="pem-label">Nome Social (opcional)</label>
              <input
                className="pem-input"
                value={formData.nomeSocial}
                onChange={(e) => set('nomeSocial', e.target.value)}
                placeholder="Nome pelo qual você prefere ser chamado(a)"
              />
              <p className="pem-hint">Se preenchido, as empresas verão esse nome em vez do nome de documento.</p>
            </div>
          )}

          {/* ── Telefone + Gênero ── */}
          <div className="pem-grid-2">
            <div className="pem-field">
              <label className="pem-label">Telefone</label>
              <input
                className="pem-input"
                value={formData.phone}
                placeholder="(11) 99999-9999"
                onChange={(e) => set('phone', formatarTelefone(e.target.value))}
                disabled
              />
            </div>
            {showFreelancerFields && (
              <div className="pem-field">
                <label className="pem-label">Gênero</label>
                <select
                  className="pem-select"
                  value={formData.gender || ''}
                  disabled
                >
                  <option value="">Selecione...</option>
                  <option value="masculino">Masculino</option>
                  <option value="feminino">Feminino</option>
                  <option value="outro">Outro</option>
                </select>
              </div>
            )}
          </div>

          {/* ── CPF / CNPJ ── */}
          <div className="pem-grid-2">
            {showFreelancerFields && (
              <div className="pem-field">
                <label className="pem-label">
                  CPF
                </label>
                <input
                  className="pem-input"
                  value={formData.cpf}
                  maxLength={14}
                  placeholder="000.000.000-00"
                  disabled
                />
                {user.cpf && (
                  <p className="pem-hint">
                    CPF já cadastrado. Para alterar, entre em contato com o suporte.
                  </p>
                )}
              </div>
            )}
            {isEmpresa && (
              <div className="pem-field">
                <label className="pem-label">CNPJ</label>
                <input
                  className="pem-input"
                  value={formData.cnpj}
                  maxLength={18}
                  onChange={(e) => set('cnpj', formatarCNPJ(e.target.value))}
                  placeholder="00.000.000/0000-00"
                  disabled={!!user.cnpj}
                />
                {user.cnpj && (
                  <p className="pem-hint">
                    CNPJ já cadastrado. Para alterar, entre em contato com o suporte.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* ── Endereço ── */}
          {/* ── CEP ── */}
          <div className="pem-field">
            <label className="pem-label">CEP</label>

            <input
              className="pem-input"
              type="text"
              placeholder="00000-000"
              value={formData.cep}
              maxLength={9}
              inputMode="numeric"
              onChange={(e) => {
                const cep = formatarCEP(e.target.value);

                setFormData((prev) => ({
                  ...prev,
                  cep,
                }));

                if (cep.replace(/\D/g, '').length === 8) {
                  buscarCEP(cep);
                }
              }}
            />

            <p className="pem-hint">
              {loadingCep
                ? "Buscando endereço..."
                : "Digite o CEP para preencher o endereço automaticamente."}
            </p>
          </div>

          {/* ── Endereço (preenchido automaticamente pelo CEP) ── */}
          {/* ── Endereço (preenchido automaticamente pelo CEP) ── */}
          <div className="pem-grid-2">
            <div className="pem-field">
              <label className="pem-label">Logradouro</label>
              <input
                className="pem-input"
                value={formData.logradouro}
                readOnly
                placeholder={!formData.logradouro ? "Não disponível para esta região" : ""}
              />
              {!formData.logradouro && (
                <p className="pem-hint pem-hint--warning">
                  ⚠️ O logradouro não está disponível para esta região.
                </p>
              )}
              {formData.logradouro && (
                <p className="pem-hint">
                  ✓ Logradouro preenchido automaticamente pelo CEP.
                </p>
              )}
            </div>
            <div className="pem-field">
              <label className="pem-label">Bairro</label>
              <input
                className="pem-input"
                value={formData.bairro}
                readOnly
                placeholder={!formData.bairro ? "Não disponível para esta região" : ""}
              />
              {!formData.bairro && (
                <p className="pem-hint pem-hint--warning">
                  ⚠️ O bairro não está disponível para esta região.
                </p>
              )}
              {formData.bairro && (
                <p className="pem-hint">
                  ✓ Bairro preenchido automaticamente pelo CEP.
                </p>
              )}
            </div>
          </div>

          <div className="pem-grid-2">
            <div className="pem-field">
              <label className="pem-label">Cidade</label>
              <input className="pem-input" value={formData.cidade} readOnly />
            </div>
            <div className="pem-field">
              <label className="pem-label">Estado</label>
              <input className="pem-input" value={formData.estado} readOnly />
            </div>
          </div>

          {/* ── Número / Complemento (o CEP não traz isso) ── */}
          <div className="pem-grid-2">

            <div className="pem-field">
              <label className="pem-label">Número</label>

              <input
                className="pem-input"
                value={formData.numero}
                onChange={(e) => set('numero', e.target.value)}
                placeholder="Ex: 123"
              />
            </div>

            <div className="pem-field">
              <label className="pem-label">Complemento</label>

              <input
                className="pem-input"
                value={formData.complemento}
                onChange={(e) => set('complemento', e.target.value)}
                placeholder="Ex: Apto 45"
              />
            </div>

          </div>

          {/* ── Bio ── */}
          <div className="pem-field">
            <label className="pem-label">Apresentação / Bio</label>
            <textarea
              className="pem-textarea"
              value={formData.bio}
              placeholder={isFreelancer
                ? 'Fale um pouco sobre sua experiência, habilidades...'
                : 'Descreva sua empresa ou estabelecimento...'}
              onChange={(e) => set('bio', e.target.value)}
              rows={3}
            />
          </div>

          {/* ── Áreas ── */}
          {showFreelancerFields && (
            <div className="pem-field">
              <label className="pem-label">Áreas de Especialização (Skills)</label>
              <input
                className="pem-input"
                value={formData.areasRaw ?? formData.areas.join(', ')}
                placeholder="Ex: Garçom, Caixa, Motoboy (separados por vírgula)"
                onChange={(e) => {
                  const raw = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    areasRaw: raw,
                    areas: raw.split(',').map(s => s.trim()).filter(Boolean),
                  }));
                }}
              />
              <p className="pem-hint">Digite suas profissões separadas por vírgula.</p>
            </div>
          )}

          {/* ── Experiências ── */}
          {showFreelancerFields && (
            <div className="pem-section">
              <div className="pem-section-header">
                <label className="pem-label">Experiências Profissionais</label>
                <button type="button" className="pem-btn pem-btn--outline pem-btn--sm" onClick={handleAddExperience}>
                  <IconPlus /> Adicionar
                </button>
              </div>

              {formData.workExperiences.length === 0 ? (
                <p className="pem-empty">Nenhuma experiência cadastrada.</p>
              ) : (
                <div className="pem-experiences">
                  {formData.workExperiences.map(exp => (
                    <div key={exp.id} className="pem-exp-card">
                      <button
                        type="button"
                        className="pem-exp-remove"
                        onClick={() => handleRemoveExperience(exp.id)}
                        title="Remover"
                      >
                        <IconTrash />
                      </button>
                      <div className="pem-grid-2">
                        <div className="pem-field">
                          <label className="pem-label pem-label--xs">Empresa/Local</label>
                          <input className="pem-input pem-input--sm" value={exp.companyName} onChange={(e) => handleUpdateExperience(exp.id, 'companyName', e.target.value)} placeholder="Nome do local" />
                        </div>
                        <div className="pem-field">
                          <label className="pem-label pem-label--xs">Cargo/Função</label>
                          <input className="pem-input pem-input--sm" value={exp.position} onChange={(e) => handleUpdateExperience(exp.id, 'position', e.target.value)} placeholder="Sua função" />
                        </div>
                        <div className="pem-field">
                          <label className="pem-label pem-label--xs">Mês</label>
                          <input className="pem-input pem-input--sm" value={exp.workMonth} onChange={(e) => handleUpdateExperience(exp.id, 'workMonth', e.target.value)} placeholder="Ex: Janeiro" />
                        </div>
                        <div className="pem-field">
                          <label className="pem-label pem-label--xs">Ano</label>
                          <input className="pem-input pem-input--sm" value={exp.workYear} onChange={(e) => handleUpdateExperience(exp.id, 'workYear', e.target.value)} placeholder="Ex: 2023" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Raio de Busca ── */}
          {showFreelancerFields && (
            <div className="pem-section">
              <div className="pem-section-header">
                <label className="pem-label">Raio de Busca para Vagas</label>
                <span className="pem-radius-value">{formData.searchRadius} km</span>
              </div>
              <input
                type="range"
                className="pem-slider"
                min={1}
                max={100}
                step={1}
                value={formData.searchRadius}
                onChange={(e) => set('searchRadius', Number(e.target.value))}
              />
              <p className="pem-hint">Você só verá vagas dentro dessa distância para evitar trabalhos muito distantes.</p>
            </div>
          )}

          {/* ── Documentação ── */}
          {showFreelancerFields && (
            <div className="pem-section">
              <div className="pem-section-header">
                <label className="pem-label pem-label--bold">Documentação para Trabalho</label>
                <span className={`pem-badge ${docStatusClass}`}>{docStatusLabel}</span>
              </div>

              {formData.documentStatus === 'rejected' && (
                <div className="pem-doc-alert">
                  ⚠️ Seu documento foi recusado. Envie novas fotos nítidas e legíveis para reanálise.
                </div>
              )}

              <div className="pem-field">
                <label className="pem-label pem-label--xs">Tipo de Documento</label>
                <select
                  className="pem-select"
                  value={formData.documentType}
                  onChange={(e) => set('documentType', e.target.value)}
                >
                  <option value="rg">RG (Identidade)</option>
                  <option value="cpf">CPF (Apenas Frente)</option>
                  <option value="cnh">CNH (Carteira de Motorista)</option>
                  <option value="cnh_digital">CNH Digital (Apenas Frente)</option>
                </select>
              </div>

              <div className="pem-doc-grid">
                {/* Frente */}
                <div className="pem-field">
                  <label className="pem-label pem-label--xs">Foto da Frente</label>
                  <div className="pem-doc-upload">
                    {formData.documentUrlFront ? (
                      <>
                        <div className="pem-doc-preview">
                          <div className="pem-doc-preview__overlay">
                            <Lock size={24} className="pem-doc-preview__lock" />
                            <span className="pem-doc-preview__text">Documento Privado</span>
                          </div>
                          <img
                            src={formData.documentUrlFront}
                            alt="Frente do documento"
                            className="pem-doc-img"
                          />
                        </div>
                        <div className="pem-doc-hover">
                          <IconUpload size={16} />
                          Alterar Imagem
                        </div>
                      </>
                    ) : (
                      <div className="pem-doc-placeholder">
                        <IconUpload size={24} />
                        <span>Enviar Frente</span>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="pem-doc-input"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadDoc(f, 'front'); }}
                      disabled={uploadingDocFront}
                    />
                    {uploadingDocFront && (
                      <div className="pem-doc-loading">
                        <div className="pem-spinner" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Verso */}
                {needsBack && (
                  <div className="pem-field">
                    <label className="pem-label pem-label--xs">Foto do Verso</label>
                    <div className="pem-doc-upload">
                      {formData.documentUrlBack ? (
                        <>
                          <div className="pem-doc-preview">
                            <div className="pem-doc-preview__overlay">
                              <Lock size={24} className="pem-doc-preview__lock" />
                              <span className="pem-doc-preview__text">Documento Privado</span>
                            </div>
                            <img
                              src={formData.documentUrlBack}
                              alt="Verso do documento"
                              className="pem-doc-img"
                            />
                          </div>
                          <div className="pem-doc-hover">
                            <IconUpload size={16} />
                            Alterar Imagem
                          </div>
                        </>
                      ) : (
                        <div className="pem-doc-placeholder">
                          <IconUpload size={24} />
                          <span className="pem-doc-missing">Falta Verso</span>
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        className="pem-doc-input"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadDoc(f, 'back'); }}
                        disabled={uploadingDocBack}
                      />
                      {uploadingDocBack && (
                        <div className="pem-doc-loading">
                          <div className="pem-spinner" />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* ─── AVISO DE SEGURANÇA ─────────────────────────────────────────── */}
              <div className="pem-security-notice">
                <div className="pem-security-notice__icon">
                  <Lock size={20} />
                </div>
                <div className="pem-security-notice__content">
                  <strong>Documentos Privados</strong>
                  <p>
                    Por questões de segurança, as imagens dos documentos ficam privadas
                    e não podem ser visualizadas diretamente no sistema.
                    Apenas o envio e a validação são permitidos.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ── Ações ── */}
          <div className="pem-actions">
            <button
              type="button"
              className="pem-btn pem-btn--outline"
              onClick={onClose}
              disabled={loading || uploading}
            >
              Cancelar
            </button>

            <button
              type="submit"
              className="pem-btn pem-btn--primary"
              disabled={loading || uploading}
            >
              {loading ? "Salvando..." : "Salvar Alterações"}
            </button>
          </div>

          {/* ── BOTÃO DE SAIR ── */}
          <div className="pem-logout-section">
            <button
              type="button"
              className={`logout-btn ${isAdmin ? 'logout-btn--admin' : ''}`}
              onClick={handleLogout}
            >
              <LogOut size={18} />
              <span>{isAdmin ? 'Sair da Conta Admin' : 'Sair da Conta'}</span>
            </button>
          </div>

          <div style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.75rem', color: '#94a3b8' }}>
            <Link to="/termos-de-uso" style={{ color: 'inherit' }}>Termos de Uso</Link>
            {' · '}
            <Link to="/politica-de-privacidade" style={{ color: 'inherit' }}>Política de Privacidade</Link>
          </div>

        </form>
      </div>
      <Toast
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ message: "", type: "success" })}
      />
    </div>
  );
};

export default FreelancerProfileModal;